import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-unused-vars -- restore with verifiedDate (see below)
import { MMMMYYYY } from '../../../utils/time';
import style from '../../../style/components/profile/emails/EmailRow.module.scss';
import EmailStatusBadge from './EmailStatusBadge';
import EmailDefaultBadge from './EmailDefaultBadge';
import UseForDropdown from './UseForDropdown';
import EmailRowActionMenu from './EmailRowActionMenu';

// email.type is the lowercased emailProviderServiceType from the BFF —
// "personal", "work", "unknown", or (rarely) "malicious". Only the first
// three get user-visible labels; anything else (including "malicious",
// which shouldn't reach this table anyway) renders blank so we don't
// mislabel it as "Personal".
const TYPE_LABEL_KEYS = {
  personal: 'walletEmails.typePersonal',
  work: 'walletEmails.typeWork',
  unknown: 'walletEmails.typeUnknown',
};

const EmailRow = ({
  email,
  onUseForChange,
  onRemove,
  onResendVerification,
  onSetDefault,
  defaultEmailPending,
}) => {
  const { t } = useTranslation();
  const typeLabelKey = TYPE_LABEL_KEYS[email.type];
  const typeLabel = typeLabelKey ? t(typeLabelKey) : '';
  // TODO(AXP-1567): Temporarily hidden. `verified_at` maps to the trust chain's
  // confirmed_at, but trust is not yet backfilled/written for emails, so the
  // date would be inaccurate at launch. Restore this (and the `date` prop
  // below) once email-api writes trust. See #eng-auth-experience thread
  // "verified at" (2026-07-20).
  // const verifiedDate = email.verified_at ? MMMMYYYY(email.verified_at) : null;
  const verifiedDate = null;

  // `data-mobile-label` flags labeled cells for the < 780px grid layout
  // (see EmailTable.module.scss). The visible mobile label comes from
  // the .mobileLabel <span> rendered inside each cell — a real DOM node
  // so screen readers read the label reliably (CSS `::before` content
  // is announced inconsistently across browsers).
  return (
    <tr className={style.row} data-testid="email-row">
      <td className={style.emailCell} data-mobile-label>
        <span className={style.mobileLabel}>{t('walletEmails.email')}</span>
        {email.address}
      </td>
      <td className={style.typeCell} data-mobile-label>
        <span className={style.mobileLabel}>{t('walletEmails.type')}</span>
        {typeLabel}
      </td>
      <td className={style.useForCell} data-mobile-label>
        <span className={style.mobileLabel}>{t('walletEmails.useFor')}</span>
        <UseForDropdown
          emailId={email.email_id}
          value={email.use_for}
          onChange={onUseForChange}
        />
      </td>
      <td className={style.statusCell} data-mobile-label>
        <span className={style.mobileLabel}>{t('walletEmails.status')}</span>
        <div className={style.statusContainer}>
          <EmailStatusBadge
            status={email.verification_status}
            date={verifiedDate}
          />
          {email.is_default && <EmailDefaultBadge />}
        </div>
      </td>
      <td className={style.actionCell}>
        <EmailRowActionMenu
          email={email}
          onRemove={onRemove}
          onResendVerification={onResendVerification}
          onSetDefault={onSetDefault}
          defaultEmailPending={defaultEmailPending}
        />
      </td>
    </tr>
  );
};

EmailRow.propTypes = {
  email: PropTypes.shape({
    email_id: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    use_for: PropTypes.string.isRequired,
    verification_status: PropTypes.string.isRequired,
    verified_at: PropTypes.string,
    is_default: PropTypes.bool,
  }).isRequired,
  onUseForChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onResendVerification: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  defaultEmailPending: PropTypes.bool,
};

export default memo(EmailRow);
